import { SkyDrawer, SkyRadioGroup, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { ConnectorAccessManager, ConnectorAccessView } from "@shared/types/connectorAccess";
import type { ConnectorAdminView } from "@shared/types/connectorsAdmin";
import type { TenantView } from "@shared/types/tenants";
import {
  ConnectorChoiceFields,
  OffersNoneDialog,
  draftFromView,
  offersNone,
  sameChoice,
  type ConnectorChoiceDraft,
} from "@/components/settings/ConnectorChoiceFields";
import {
  useConnectorAvailability,
  usePlatformConnectorAccess,
  useSavePlatformConnectorAccess,
} from "@/hooks/use-connector-access";
import { useConnectorsAdmin } from "@/hooks/use-connectors-admin";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { ErrorState } from "./ScreenState";

/**
 * Tenants > Connectors (T-51): a PlatformAdmin sets a tenant's connector choice for NEW
 * connections, and who manages it from then on: "Set by the tenant" (the default; the tenant's
 * admins change it in Settings > Connectors) or "Locked by Skypoint" (this list applies and the
 * tenant's admins read it only). "Offered now" is exactly what the tenant's New connection picker
 * shows. Every connector of the deployment can be ticked; one that is not available stays hidden
 * from the tenant until it is made available again.
 */

function displayNames(names: string[], catalog: ConnectorAdminView[]): string {
  if (names.length === 0) return "No connector: nobody in this tenant can create a new connection.";
  return names
    .map((name) => catalog.find((connector) => connector.name === name)?.displayName ?? name)
    .join(", ");
}

function LoadedChoice({
  tenant,
  view,
  catalog,
  notAvailable,
  onClose,
}: {
  tenant: TenantView;
  view: ConnectorAccessView;
  catalog: ConnectorAdminView[];
  notAvailable: Set<string>;
  onClose: () => void;
}) {
  const save = useSavePlatformConnectorAccess(tenant.id);
  const notice = useNotice();
  const saveError = useInlineError();
  const [draft, setDraft] = useState<ConnectorChoiceDraft>(() => draftFromView(view));
  const [managedBy, setManagedBy] = useState<ConnectorAccessManager>(view.managedBy);
  const [confirmingNone, setConfirmingNone] = useState(false);

  const options = catalog.map((connector) => ({
    name: connector.name,
    displayName: connector.displayName,
    ...(!connector.isActive || notAvailable.has(connector.name)
      ? { description: "Not available: not offered to any tenant" }
      : {}),
  }));
  const changed = managedBy !== view.managedBy || !sameChoice(draft, draftFromView(view));

  function submit() {
    saveError.clear();
    save.mutate(
      {
        mode: draft.mode,
        connectors: draft.connectors,
        hidden: draft.hidden,
        managedBy,
      },
      {
        onSuccess: (saved) => {
          setDraft(draftFromView(saved));
          setManagedBy(saved.managedBy);
          notice.success(`Connectors saved for ${tenant.displayName}.`);
          onClose();
        },
        onError: (error) => saveError.show(error, "The connector choice was not saved."),
      },
    );
  }

  return (
    <>
      <SkyDrawer
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        position="end"
        size="medium"
        headerTitle={`Connectors for ${tenant.displayName}`}
        subtitle="Which connectors this tenant offers for new connections. Existing connections keep working whatever is chosen."
        testId="tenant-connectors-drawer"
        footerButtons={[
          {
            label: "Save",
            appearance: "primary",
            disabled: !changed,
            loading: save.isPending,
            onClick: () => (offersNone(draft, options) ? setConfirmingNone(true) : submit()),
          },
          { label: "Close", appearance: "subtle", onClick: onClose },
        ]}
      >
        <div className="flex flex-col gap-4">
          <InlineErrorBar
            error={saveError.error}
            title="The connector choice was not saved"
            onDismiss={saveError.clear}
            testId="tenant-connectors-error"
          />
          <div data-testid="tenant-connectors-offered">
            <SkyText size={300} weight="semibold" block>
              Offered now
            </SkyText>
            <SkyText size={200} color="muted" block>
              {displayNames(view.effective, catalog)}
            </SkyText>
          </div>
          <SkyRadioGroup
            testId="tenant-connectors-managed-by"
            label="Who manages it"
            name="tenant-connectors-managed-by"
            value={managedBy}
            onChange={(value) => setManagedBy(value as ConnectorAccessManager)}
            options={[
              {
                value: "tenant",
                label: "Set by the tenant",
                description: "The tenant's admins choose in Settings > Connectors.",
              },
              {
                value: "platform",
                label: "Locked by Skypoint",
                description: "This list applies. The tenant's admins see it read-only.",
              },
            ]}
          />
          <ConnectorChoiceFields
            draft={draft}
            options={options}
            onChange={setDraft}
            tenantName={tenant.displayName}
            testIdPrefix="tenant-connectors"
          />
        </div>
      </SkyDrawer>
      <OffersNoneDialog
        open={confirmingNone}
        onOpenChange={setConfirmingNone}
        onConfirm={() => {
          setConfirmingNone(false);
          submit();
        }}
        tenantName={tenant.displayName}
        testId="tenant-connectors-none-dialog"
      />
    </>
  );
}

export function TenantConnectorsDrawer({
  tenant,
  onClose,
}: {
  /** The tenant whose choice is open; null closes the drawer. */
  tenant: TenantView | null;
  onClose: () => void;
}) {
  const view = usePlatformConnectorAccess(tenant?.id ?? null);
  // Nothing is fetched until a tenant's drawer opens: the Tenants page itself never needs it.
  const catalog = useConnectorsAdmin(tenant !== null);
  const availability = useConnectorAvailability(tenant !== null);

  if (!tenant) return null;
  if (view.isError || catalog.isError || availability.isError) {
    return (
      <SkyDrawer
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        position="end"
        size="medium"
        headerTitle={`Connectors for ${tenant.displayName}`}
        testId="tenant-connectors-drawer"
      >
        <ErrorState
          title="The connector choice did not load"
          error={view.error ?? catalog.error ?? availability.error}
          onRetry={() => {
            void view.refetch();
            void catalog.refetch();
            void availability.refetch();
          }}
        />
      </SkyDrawer>
    );
  }
  if (!view.data || !catalog.data || !availability.data) return null;
  return (
    <LoadedChoice
      key={`${tenant.id}-${view.data.updatedAt ?? "never"}`}
      tenant={tenant}
      view={view.data}
      catalog={catalog.data}
      notAvailable={new Set(availability.data.notAvailable)}
      onClose={onClose}
    />
  );
}
